import { apiSlice } from './apiSlice';

// NOTE (typed as `any` deliberately): these user endpoints return the legacy
// admin envelope — the list body is `{ data: User[], ...pagination }`, while
// the edit/profile endpoints return module-specific keys — and consumers
// (UsersView, useUsers, profile views) read the nested fields dynamically.
// Concrete User/UserListResponse DTOs must land together with a pass over
// those consumers; keep this slice `any`-typed until then, and do not copy
// the `any` into new code.
export const usersApi = apiSlice.injectEndpoints({
   // Adjust baseUrl to match your application configuration
  endpoints: (builder) => ({
    getUsers: builder.query<any, Record<string, any> | void>({
      query: (params) => ({
        url: '/api/users',
        params: params || {},
      }),
      // Only rows that actually carry an id get a per-entity tag: an entry
      // without one used to produce `{ type: 'User', id: undefined }`, which
      // RTK Query folds into the type-level bucket and silently invalidates
      // every cached user query instead of that one row.
      providesTags: (result) =>
        result && result.data && Array.isArray(result.data)
          ? [
              ...result.data
                .filter((user: any) => user && user.id !== undefined && user.id !== null)
                .map((user: any) => ({ type: 'User' as const, id: user.id })),
              { type: 'User', id: 'LIST' },
            ]
          : [{ type: 'User', id: 'LIST' }],
    }),
    getUser: builder.query<any, string | number>({
      query: (id) => ({ url: `/api/users/${id}` }),
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),
    createUser: builder.mutation<any, Record<string, any>>({
      query: (body) => ({
        url: '/api/users',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),
    updateUser: builder.mutation<any, { id: string | number; data: Record<string, any> }>({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          const multipart = new FormData();
          for (const [key, value] of data.entries()) multipart.append(key, value);
          multipart.set('_method', 'PUT');
          return { url: `/api/users/${id}`, method: 'POST', data: multipart };
        }
        return { url: `/api/users/${id}`, method: 'PUT', data };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }, { type: 'User', id: 'LIST' }],
    }),
    deleteUser: builder.mutation<void, string | number>({
      query: (id) => ({
        url: `/api/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'User', id }, { type: 'User', id: 'LIST' }],
    }),
    getUserForEdit: builder.query<any, string | number>({
      query: (id) => ({ url: `/api/users/${id}/edit` }),
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),
    updateUserStatus: builder.mutation<any, { id: string | number; isActive: boolean }>({
      query: ({ id, isActive }) => ({
        url: `/api/users_switch_activated/${id}`,
        method: 'PUT',
        data: { statut: isActive ? 1 : 0 },
      }),
      // A status toggle changes what the users list shows (active filter/counts).
      // A bare 'User' would cover LIST, but the sibling mutations scope the list
      // explicitly — keep the same shape so none of them can drift.
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }, { type: 'User', id: 'LIST' }],
    }),
    getProfile: builder.query<any, void>({
      query: () => ({ url: '/api/Get_user_profile' }),
      providesTags: ['User'],
    }),
    updateProfile: builder.mutation<any, { id: string | number; data: Record<string, any> }>({
      query: ({ id, data }) => ({
        url: `/api/update_user_profile/${id}`,
        method: 'PUT',
        data: data,
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetUserQuery,
  useLazyGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetUserForEditQuery,
  useUpdateUserStatusMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
} = usersApi;
